/**
 * Content Protection Utility
 * Chặn chuột phải, long press, copy, select text và các tính năng copy khác
 */

export class ContentProtection {
  private static instance: ContentProtection;
  private isEnabled = false;
  private eventHandlers: Map<string, Array<(data: any) => void>> = new Map();

  private constructor() {}

  public static getInstance(): ContentProtection {
    if (!ContentProtection.instance) {
      ContentProtection.instance = new ContentProtection();
    }
    return ContentProtection.instance;
  }

  /**
   * Kích hoạt content protection
   */
  public enable(): void {
    if (this.isEnabled) return;
    
    console.log('[ContentProtection] 🔒 Enabling content protection...');
    
    // Chặn chuột phải (context menu)
    document.addEventListener('contextmenu', this.preventContextMenu, { passive: false });
    
    // Chặn select text
    document.addEventListener('selectstart', this.preventSelect, { passive: false });
    document.addEventListener('dragstart', this.preventDrag, { passive: false });
    
    // Chặn keyboard shortcuts
    document.addEventListener('keydown', this.preventKeyboardShortcuts, { passive: false });
    
    // Chặn F12 (Developer Tools)
    document.addEventListener('keydown', this.preventF12, { passive: false });
    
    // Mobile: Chặn long press context menu
    document.addEventListener('touchstart', this.preventMobileLongPress, { passive: false });
    document.addEventListener('touchend', this.clearMobileLongPress, { passive: false });
    document.addEventListener('touchmove', this.clearMobileLongPress, { passive: false });
    
    // Chặn text selection với CSS
    this.addProtectionCSS();
    
    // Chặn print
    window.addEventListener('beforeprint', this.preventPrint, { passive: false });
    
    this.isEnabled = true;
    console.log('[ContentProtection] ✅ Content protection enabled');
  }

  /**
   * Tắt content protection
   */
  public disable(): void {
    if (!this.isEnabled) return;
    
    console.log('[ContentProtection] 🔓 Disabling content protection...');
    
    document.removeEventListener('contextmenu', this.preventContextMenu);
    document.removeEventListener('selectstart', this.preventSelect);
    document.removeEventListener('dragstart', this.preventDrag);
    document.removeEventListener('keydown', this.preventKeyboardShortcuts);
    document.removeEventListener('keydown', this.preventF12);
    document.removeEventListener('touchstart', this.preventMobileLongPress);
    document.removeEventListener('touchend', this.clearMobileLongPress);
    document.removeEventListener('touchmove', this.clearMobileLongPress);
    window.removeEventListener('beforeprint', this.preventPrint);
    
    this.removeProtectionCSS();
    
    this.isEnabled = false;
    console.log('[ContentProtection] ✅ Content protection disabled');
  }

  private longPressTimer: NodeJS.Timeout | null = null;

  /**
   * Chặn context menu (chuột phải)
   */
  private preventContextMenu = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[ContentProtection] 🚫 Right-click blocked');
    this.emit('protection_triggered', { type: 'rightclick' });
  };

  /**
   * Chặn text selection
   */
  private preventSelect = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[ContentProtection] 🚫 Text selection blocked');
  };

  /**
   * Chặn drag and drop
   */
  private preventDrag = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[ContentProtection] 🚫 Drag blocked');
  };

  /**
   * Chặn keyboard shortcuts copy/paste/save
   */
  private preventKeyboardShortcuts = (e: KeyboardEvent): void => {
    // Chặn Ctrl+A (Select All)
    if (e.ctrlKey && e.key === 'a') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 Ctrl+A blocked');
      return;
    }

    // Chặn Ctrl+C (Copy)
    if (e.ctrlKey && e.key === 'c') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 Ctrl+C blocked');
      return;
    }

    // Chặn Ctrl+V (Paste)
    if (e.ctrlKey && e.key === 'v') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 Ctrl+V blocked');
      return;
    }

    // Chặn Ctrl+S (Save)
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 Ctrl+S blocked');
      return;
    }

    // Chặn Ctrl+P (Print)
    if (e.ctrlKey && e.key === 'p') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 Ctrl+P blocked');
      return;
    }

    // Chặn Ctrl+Shift+I (Developer Tools)
    if (e.ctrlKey && e.shiftKey && e.key === 'I') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 Ctrl+Shift+I blocked');
      return;
    }

    // Chặn Ctrl+Shift+J (Console)
    if (e.ctrlKey && e.shiftKey && e.key === 'J') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 Ctrl+Shift+J blocked');
      return;
    }

    // Chặn Ctrl+U (View Source)
    if (e.ctrlKey && e.key === 'u') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 Ctrl+U blocked');
      return;
    }
  };

  /**
   * Chặn F12 (Developer Tools)
   */
  private preventF12 = (e: KeyboardEvent): void => {
    if (e.key === 'F12') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 F12 blocked');
      this.emit('protection_triggered', { type: 'devtools' });
      return;
    }
  };

  /**
   * Chặn mobile long press context menu
   */
  private preventMobileLongPress = (e: TouchEvent): void => {
    // Clear any existing timer
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
    }

    // Set timer to detect long press
    this.longPressTimer = setTimeout(() => {
      e.preventDefault();
      e.stopPropagation();
      console.log('[ContentProtection] 🚫 Mobile long press blocked');
    }, 500); // 500ms threshold for long press
  };

  /**
   * Clear mobile long press timer
   */
  private clearMobileLongPress = (): void => {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  };

  /**
   * Chặn print
   */
  private preventPrint = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[ContentProtection] 🚫 Print blocked');
  };

  /**
   * Thêm CSS để chặn selection
   */
  private addProtectionCSS(): void {
    const styleId = 'content-protection-styles';
    
    // Check if styles already exist
    if (document.getElementById(styleId)) {
      return;
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Chặn text selection */
      * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        
        -webkit-touch-callout: none !important;
        -webkit-tap-highlight-color: transparent !important;
      }
      
      /* Chặn drag images */
      img {
        -webkit-user-drag: none !important;
        -khtml-user-drag: none !important;
        -moz-user-drag: none !important;
        -o-user-drag: none !important;
        user-drag: none !important;
        pointer-events: none !important;
      }
      
      /* Chặn highlight trên mobile */
      * {
        -webkit-tap-highlight-color: rgba(0,0,0,0) !important;
        -webkit-focus-ring-color: rgba(0,0,0,0) !important;
        outline: none !important;
      }
      
      /* Allow user interaction for interactive elements */
      button, input, textarea, select, a {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        pointer-events: auto !important;
      }
      
      /* Ensure audio controls still work */
      audio, video {
        pointer-events: auto !important;
      }
      
      /* Special handling for form inputs */
      input[type="text"], input[type="email"], input[type="password"], textarea {
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
      }
    `;
    
    document.head.appendChild(style);
    console.log('[ContentProtection] 🎨 Protection CSS added');
  }

  /**
   * Xóa CSS protection
   */
  private removeProtectionCSS(): void {
    const styleElement = document.getElementById('content-protection-styles');
    if (styleElement) {
      styleElement.remove();
      console.log('[ContentProtection] 🎨 Protection CSS removed');
    }
  }

  /**
   * Check if protection is enabled
   */
  public isProtectionEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Add event listener
   */
  public on(event: string, handler: (data: any) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  /**
   * Remove event listener
   */
  public off(event: string, handler: (data: any) => void): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Emit event
   */
  private emit(event: string, data?: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  /**
   * Temporarily allow selection for specific element
   */
  public allowSelectionFor(element: HTMLElement, duration: number = 5000): void {
    element.style.setProperty('-webkit-user-select', 'text', 'important');
    element.style.setProperty('-moz-user-select', 'text', 'important');
    element.style.setProperty('-ms-user-select', 'text', 'important');
    element.style.setProperty('user-select', 'text', 'important');
    
    setTimeout(() => {
      element.style.removeProperty('-webkit-user-select');
      element.style.removeProperty('-moz-user-select');
      element.style.removeProperty('-ms-user-select');
      element.style.removeProperty('user-select');
    }, duration);
  }
}

// Export singleton instance
export const contentProtection = ContentProtection.getInstance();

// Auto-enable in production
if (process.env.NODE_ENV === 'production') {
  // Enable protection when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      contentProtection.enable();
    });
  } else {
    contentProtection.enable();
  }
} 